import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import { salesWhatsappLink } from '@constants/contact';
import { useTheme } from '@contexts/ThemeContext';
import { clearJarvisSession } from '@utils/auth-session';
import { useLanguage } from '@contexts/LanguageContext';
import {
  DEMO_DOC_LABEL,
  DEMO_ENDED_PARAM,
  DEMO_ENDED_VALUE,
  DEMO_ME_QUERY_KEY,
  type DemoDocType,
  type DemoUsage,
  demoTimeLeft,
  isDemoSession,
} from '@utils/demo-account';

const DOC_ORDER: DemoDocType[] = ['PIB', 'PEB', 'MANIFEST'];

/**
 * A small, always-visible strip for demo accounts: how long the demo has
 * left and how much of the extraction quota is used. Renders nothing for
 * everyone else.
 */
export default function DemoBanner() {
  const demo = isDemoSession();
  const { colors } = useTheme();
  const { t } = useLanguage();
  const [now, setNow] = useState(() => Date.now());

  const usage = useQuery({
    queryKey: DEMO_ME_QUERY_KEY,
    queryFn: () => axiosInstance.get<{ data: DemoUsage | null }>(apiUrl.demo.me).then((r) => r.data.data),
    enabled: demo,
    refetchInterval: 60_000,
  });

  // Tick the countdown every 30s; the quota is refreshed by the query above.
  useEffect(() => {
    if (!demo) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [demo]);

  const data = usage.data;
  const left = data ? demoTimeLeft(data.expiresAt, now) : null;

  // The window closed while the app was open: end the session the same way
  // the API client does when a request is refused. The session is cleared
  // first, or the login page would send the still-valid token straight back.
  useEffect(() => {
    if (demo && data && left === null) {
      clearJarvisSession();
      window.location.href = `/auth?${DEMO_ENDED_PARAM}=${DEMO_ENDED_VALUE}`;
    }
  }, [demo, data, left]);

  if (!demo || !data) return null;

  return (
    <div
      className="fixed bottom-4 z-[9999] flex justify-center pointer-events-none"
      style={{ left: 88, right: 0 }}
      role="status"
    >
      <div
        className="pointer-events-auto flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border px-4 py-2 text-xs shadow-lg"
        style={{ backgroundColor: colors.card, borderColor: colors.primary, color: colors.text }}
      >
        <span
          className="rounded-full px-2 py-0.5 font-semibold"
          style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}
        >
          {t('demo.badge')}
        </span>
        {left && (
          <span>
            {t('demo.endsIn')} <strong className="tabular-nums">{left}</strong>
          </span>
        )}
        <span aria-hidden style={{ opacity: 0.4 }}>
          ·
        </span>
        <span title={t('demo.quotaHint')}>
          {DOC_ORDER.map((type, i) => {
            const q = data.quota[type];
            const full = q.used >= q.limit;
            return (
              <span key={type} style={full ? { color: colors.warning } : undefined}>
                {i > 0 && ' · '}
                {DEMO_DOC_LABEL[type]}{' '}
                <span className="tabular-nums">
                  {q.used}/{q.limit}
                </span>
              </span>
            );
          })}
        </span>
        <a
          href={salesWhatsappLink(t('demo.salesMessage'))}
          target="_blank"
          rel="noreferrer"
          className="font-semibold underline"
          style={{ color: colors.primary }}
        >
          {t('demo.contactSales')}
        </a>
      </div>
    </div>
  );
}
